import { clsx } from 'clsx'
import { format } from 'date-fns'
import { formatNumber } from '@/lib/format'

const TRANSACTIONAL_LIMITS: Record<string, number> = {
  free: 10_000,
  starter: 25_000,
  pro: 75_000,
  growth: 300_000,
  scale: 2_000_000,
}

const MARKETING_LIMITS: Record<string, number> = {
  free: 3_000,
  starter: 10_000,
  pro: 50_000,
  growth: 150_000,
  scale: 500_000,
}

interface PlanCardProps {
  tier: string
  marketingTier: string
  emailsSent: number
  marketingEmailsSent: number
  contactCount: number
  periodEnd: string | null
}

function usagePercent(sent: number, limit: number): number {
  if (limit <= 0) return 0
  return Math.min((sent / limit) * 100, 100)
}

function barColor(percent: number): string {
  if (percent >= 90) return 'bg-danger'
  if (percent >= 70) return 'bg-warning'
  return 'bg-brand-500'
}

function UsageBar({
  label,
  sent,
  limit,
}: {
  label: string
  sent: number
  limit: number
}): React.JSX.Element {
  const pct = usagePercent(sent, limit)
  return (
    <div className="flex-1 min-w-0">
      <div className="flex justify-between text-xs">
        <span className="text-text-secondary">{label}</span>
        <span className="font-medium text-text-primary">
          {formatNumber(sent)} / {formatNumber(limit)}
        </span>
      </div>
      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-surface-2">
        <div
          className={clsx('h-full rounded-full transition-all duration-500', barColor(pct))}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function TierBadge({ label, tier }: { label: string; tier: string }): React.JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-1 px-3 py-1 text-xs font-medium text-text-primary">
      {label}: <span className="capitalize text-brand-500">{tier}</span>
    </span>
  )
}

export function PlanCard({
  tier,
  marketingTier,
  emailsSent,
  marketingEmailsSent,
  contactCount,
  periodEnd,
}: PlanCardProps): React.JSX.Element {
  const txLimit = TRANSACTIONAL_LIMITS[tier] ?? 10_000
  const mktLimit = MARKETING_LIMITS[marketingTier] ?? 3_000
  const contactLimit = Math.max(txLimit, mktLimit)
  const showUpgrade = tier === 'free' || marketingTier === 'free'

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TierBadge label="Transactional" tier={tier} />
          <TierBadge label="Marketing" tier={marketingTier} />
        </div>
        {showUpgrade && (
          <a href="/billing" className="text-sm font-medium text-brand-500 hover:text-brand-600">
            Upgrade &rarr;
          </a>
        )}
      </div>

      <div className="mt-4 flex gap-6">
        <UsageBar label="API Emails" sent={emailsSent} limit={txLimit} />
        <UsageBar label="Campaign Emails" sent={marketingEmailsSent} limit={mktLimit} />
      </div>

      <div className="mt-3">
        <UsageBar label="Contacts" sent={contactCount} limit={contactLimit} />
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-text-tertiary">
        <span>
          {periodEnd
            ? `Resets ${format(new Date(periodEnd), 'MMM d, yyyy')}`
            : 'Resets at end of period'}
        </span>
        <a href="/billing" className="font-medium text-text-secondary hover:text-text-primary">
          Manage billing &rarr;
        </a>
      </div>
    </div>
  )
}
